import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Alert from '@material-ui/lab/Alert';
import moment from 'moment';
import { createElderServiceEvent, updateElderServiceEvent } from 'graphql/mutations';
import { request } from 'utilities/graph';
import { toastr } from 'react-redux-toastr';
import cache from 'utilities/cache';

const useStyles = makeStyles((theme) => ({
  dialog: {
    minWidth: 500,
  },
  dateRange: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(1),
  },
  formSection: {
    marginBottom: theme.spacing(2),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
}));

export default function ElderReminderDialog({
  open,
  onClose,
  clientId,
  elderId,
  dateRange,
  onSuccess,
  editingEvent = null,
}) {
  const classes = useStyles();
  const [description, setDescription] = useState('');
  const [note, setNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentDate, setCurrentDate] = useState('');

  const isEditing = !!editingEvent;

  useEffect(() => {
    if (editingEvent) {
      setDescription(editingEvent.description || '');
      setNote(editingEvent.note || '');
      setCurrentDate(moment(editingEvent.startedAt).format('YYYY-MM-DD'));
    } else {
      setDescription('');
      setNote('');
      setCurrentDate(moment(dateRange?.start).format('YYYY-MM-DD'));
    }
  }, [editingEvent, dateRange]);

  const handleSubmit = async () => {
    if (!description.trim()) {
      toastr.warning('請輸入提醒內容');
      return;
    }

    if (!currentDate) {
      toastr.warning('請選擇提醒日期');
      return;
    }

    setIsSubmitting(true);
    try {
      const now = moment().toISOString();
      const userName = cache.get('app:userName');

      if (isEditing) {
        await request(updateElderServiceEvent, {
          input: {
            id: editingEvent.id,
            startedAt: moment(currentDate).toISOString(),
            description: description.trim(),
            note: note.trim(),
            updatedAt: now,
            updatedBy: userName,
          },
        });
      } else {
        await request(createElderServiceEvent, {
          input: {
            clientId: clientId,
            elderId,
            type: 'reminder',
            startedAt: moment(currentDate).toISOString(),
            description: description.trim(),
            note: note.trim(),
            status: '使用中',
            createdAt: now,
            createdBy: userName,
            updatedAt: now,
            updatedBy: userName,
          },
        });
      }

      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      console.error(`${isEditing ? '更新' : '建立'}提醒失敗:`, error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setDescription('');
      setNote('');
      setCurrentDate('');
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      className={classes.dialog}
    >
      <DialogTitle>
        {isEditing ? '修改提醒' : '設定提醒'}
      </DialogTitle>

      <DialogContent>
        <div className={classes.dateRange}>
          <Typography variant="h6" gutterBottom>
            提醒日期
          </Typography>
          {isEditing ? (
            <TextField
              label="提醒日期"
              type="date"
              value={currentDate}
              onChange={(e) => setCurrentDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />
          ) : (
            <Typography variant="body1">
              {moment(dateRange?.start).format('YYYY-MM-DD')}
            </Typography>
          )}
        </div>

        <div className={classes.formSection}>
          <Typography variant="h6" gutterBottom>
            提醒內容 *
          </Typography>
          <TextField
            fullWidth
            placeholder="請輸入提醒內容..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            style={{ marginBottom: 16 }}
          />
        </div>

        <div className={classes.formSection}>
          <Typography variant="h6" gutterBottom>
            備註
          </Typography>
          <TextField
            fullWidth
            placeholder="請輸入備註..."
            value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline
          />
        </div>

        <Alert severity="info" style={{ marginTop: 16 }}>
          設定提醒不會影響任何訂單。
        </Alert>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          取消
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={isSubmitting || !description.trim() || !currentDate}
        >
          {isSubmitting ? '處理中...' : (isEditing ? '更新' : '送出')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ElderReminderDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  clientId: PropTypes.string.isRequired,
  elderId: PropTypes.string.isRequired,
  dateRange: PropTypes.shape({
    start: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
    end: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  }),
  onSuccess: PropTypes.func,
  editingEvent: PropTypes.object,
};
